// Viewport questions the layout asks, answered in one place.
//
// Every breakpoint the app reacts to lives here rather than as a media query
// string written inline at each call site. Two components disagreeing by
// 32px produces a layout that is broken only inside that gap, which is
// exactly the kind of defect nobody finds by resizing a window.

import { useMediaQuery } from "@astryxdesign/core/hooks";

// Shared by every management table (Cameras/Projects/Users/Roles) — drops
// to "compact" below 768px so rows don't force horizontal scrolling on a
// narrow viewport quite as aggressively. A fixed density value otherwise
// has no notion of the viewport at all.
export function useResponsiveTableDensity(): "compact" | "balanced" {
  const isNarrow = useMediaQuery("(max-width: 768px)");
  return isNarrow ? "compact" : "balanced";
}

/** True on a phone-sized viewport.
 *
 *  One definition shared by every page that changes shape on mobile, rather
 *  than four hand-written media queries that can drift apart — a table that
 *  drops columns at one breakpoint while its page header reflows at another
 *  produces a layout that is broken only in the gap between them.
 *
 *  640px rather than the 768px above because these are different questions:
 *  density is a comfort setting that can ease in early, while dropping a
 *  column removes information and should wait until the row genuinely will
 *  not fit. 640px is also where App.tsx already folds the top nav, so the
 *  whole frame changes shape at one width.
 */
export function useIsNarrowViewport(): boolean {
  return useMediaQuery("(max-width: 640px)");
}

/** True below the width where the live wall's own chrome — the folder
 *  panel, the 3/4/5-column grid options — stops fitting comfortably.
 *
 *  768px, not the 640px above: gates four separate behaviors on
 *  LiveGridPage (the folder panel vs. a Site selector, which grid-size
 *  options render, the auto-column clamp, and FocusDialog's PTZ pad
 *  placement), and used to be written inline there as its own
 *  `useMediaQuery("(max-width: 768px)")` — exactly the "two components
 *  disagreeing by 32px" case this module's own header comment warns
 *  about, since it sat 128px from useIsNarrowViewport's 640px with
 *  nothing here to notice if either one drifted. */
export function useIsTabletViewport(): boolean {
  return useMediaQuery("(max-width: 768px)");
}
